import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import type { AccessibleBoard } from '@/entities/collab';
import type { Database } from '@/entities/database';
import type { Workspace } from '@/entities/workspace';
import { originStateFrom } from '@/shared/lib/origin';
import { Icons } from '@/shared/ui';

import styles from './HomeDashboard.module.css';

/** Results shown per group before the rest sit behind the group's "all N" link. */
const PER_GROUP = 5;

/**
 * Quick results of the ONE Home search: workspaces, boards and databases that match the typed
 * text (names; boards and databases also by their workspace's name), each with its type icon and
 * parent workspace. It works on the lists the server already limited to what the viewer may open
 * and ignores the type / owner filters below on purpose - a "boards only" filter must not make
 * the search blind to a database - which the note under the heading says. Longer lists end in a
 * link to the full catalog (carrying the text), so a cut list never passes for complete.
 */
export function HomeQuickSearch({
  query,
  workspaces,
  boards,
  databases,
}: {
  query: string;
  workspaces: readonly Workspace[];
  boards: readonly AccessibleBoard[];
  databases: readonly Database[];
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const needle = query.trim().toLocaleLowerCase();
  const personalLabel = t('home.personalWorkspace');
  const nameOf = useMemo(
    () =>
      new Map(
        workspaces.map((w) => [w.id, w.workspace_type === 'personal' ? personalLabel : w.name]),
      ),
    [workspaces, personalLabel],
  );
  const has = (...texts: (string | null | undefined)[]) =>
    texts.some((text) => (text ?? '').toLocaleLowerCase().includes(needle));
  const foundWorkspaces = useMemo(
    () =>
      workspaces.filter((w) => has(w.name, w.workspace_type === 'personal' ? personalLabel : '')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaces, needle, personalLabel],
  );
  const foundBoards = useMemo(
    () =>
      boards.filter((b) =>
        has(b.name, b.workspace_type === 'personal' ? personalLabel : b.workspace_name),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boards, needle, personalLabel],
  );
  const foundDatabases = useMemo(
    () => databases.filter((d) => has(d.name, nameOf.get(d.workspace_id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [databases, needle, nameOf],
  );
  if (needle === '') return null;
  const total = foundWorkspaces.length + foundBoards.length + foundDatabases.length;
  const q = encodeURIComponent(query.trim());
  const group = (
    key: 'workspaces' | 'boards' | 'databases',
    icon: React.ReactNode,
    count: number,
    catalog: string,
    items: React.ReactNode,
  ) =>
    count > 0 && (
      <section className={styles.quickGroup} aria-label={t(`home.quick.${key}`)}>
        <h3 className={styles.quickHeading}>
          {icon} {t(`home.quick.${key}`)}
          <span className={styles.count}>{count}</span>
        </h3>
        <ul className={styles.quickList}>{items}</ul>
        {count > PER_GROUP && (
          <Link
            to={`${catalog}?q=${q}`}
            className={styles.quickAll}
            data-testid={`quick-all-${key}`}
          >
            {t('home.quick.viewAll', { count })}
          </Link>
        )}
      </section>
    );
  return (
    <section className={styles.quick} aria-label={t('home.quick.title')} data-testid="home-quick">
      <p className={styles.quickNote}>{t('home.quick.scopeNote')}</p>
      {total === 0 && (
        <p className={styles.quickNone}>{t('home.quick.none', { query: query.trim() })}</p>
      )}
      <div className={styles.quickGroups}>
        {group(
          'workspaces',
          <Icons.workspace />,
          foundWorkspaces.length,
          '/app/browse/workspaces',
          foundWorkspaces.slice(0, PER_GROUP).map((w) => (
            <li key={w.id}>
              <Link to={`/app/workspaces/${w.id}`} className={styles.quickItem}>
                <span className={styles.quickName}>
                  {w.workspace_type === 'personal' ? personalLabel : w.name}
                </span>
              </Link>
            </li>
          )),
        )}
        {group(
          'boards',
          <Icons.board />,
          foundBoards.length,
          '/app/browse/boards',
          foundBoards.slice(0, PER_GROUP).map((b) => (
            <li key={b.id}>
              <Link
                to={`/app/boards/${b.id}`}
                state={originStateFrom(location)}
                className={styles.quickItem}
              >
                <span className={styles.quickName}>{b.name}</span>
                <span className={styles.quickWorkspace}>
                  {t('home.workspaceOf', {
                    name:
                      b.workspace_type === 'personal' ? personalLabel : (b.workspace_name ?? '—'),
                  })}
                </span>
              </Link>
            </li>
          )),
        )}
        {group(
          'databases',
          <Icons.viewTable />,
          foundDatabases.length,
          '/app/browse/databases',
          foundDatabases.slice(0, PER_GROUP).map((d) => (
            <li key={d.id}>
              <Link
                to={`/app/databases/${d.id}`}
                state={originStateFrom(location)}
                className={styles.quickItem}
              >
                <span className={styles.quickName}>{d.name}</span>
                <span className={styles.quickWorkspace}>
                  {t('home.workspaceOf', { name: nameOf.get(d.workspace_id) ?? '—' })}
                </span>
              </Link>
            </li>
          )),
        )}
      </div>
    </section>
  );
}
